import React from "react";
import {
    IonAccordion,
    IonAccordionGroup,
    IonBadge,
    IonButton,
    IonButtons,
    IonCol,
    IonContent,
    IonGrid,
    IonHeader,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonRow,
    IonSelect,
    IonSelectOption,
    IonText,
    IonTitle,
    IonToggle,
    IonToolbar,
} from "@ionic/react";
import type { ToggleCustomEvent } from "@ionic/core/components";
import { pencilOutline, pencilSharp } from "ionicons/icons";
import { US_REGION_NAMES } from "../../../constants/usRegions";
import type {
    AgentLicenseUiModel,
    EditableAgentLicenseDraft,
    SettingsStateOption,
    Tier2AgentUiModel,
} from "../AcaSettingsTypes";

const ALL_STATES: SettingsStateOption[] = Object.entries(US_REGION_NAMES).map(([code, name]) => ({code, name,}));

interface ModalManageTier2LicensesProps {
    modal: {
        isOpen: boolean;
        onClose: () => void;
    };
    data: {
        selectedAgentForLicenses: Tier2AgentUiModel | null;
    };
    npnEditor: {
        isEditingAgentNpn: boolean;
        setIsEditingAgentNpn: (value: boolean) => void;
        agentNpnDraft: string;
        setAgentNpnDraft: (value: string) => void;
        agentNpnDraftError: string;
        setAgentNpnDraftError: (value: string) => void;
    };
    newLicenseForm: {
        newLicenseState: string;
        setNewLicenseState: (value: string) => void;
        newLicenseNumber: string;
        setNewLicenseNumber: (value: string) => void;
        newLicenseExpiry: string;
        setNewLicenseExpiry: (value: string) => void;
    };
    editingLicense: {
        editingLicenseId: number | null;
        editingLicenseDraft: EditableAgentLicenseDraft | null;
        setEditingLicenseDraft: React.Dispatch<React.SetStateAction<EditableAgentLicenseDraft | null>>;
        editingLicenseDraftError: string;
        setEditingLicenseDraftError: (value: string) => void;
    };
    actions: {
        validateGlobalNpn: (value: string) => string;
        presentAlert: (options: any) => void;
        onSaveAgentNpn: (newNpnRaw: string) => void | Promise<void>;
        onToggleTier2AgentActive: (
            agent: Tier2AgentUiModel,
            nextActive: boolean
        ) => void | Promise<void>;
        onAddAgentLicense: () => void | Promise<void>;
        beginEditExistingLicense: (license: AgentLicenseUiModel) => void;
        cancelEditExistingLicense: () => void;
        saveExistingLicenseEdits: (license: AgentLicenseUiModel) => void | Promise<void>;
    };
}

const ModalManageTier2Licenses: React.FC<ModalManageTier2LicensesProps> = ({modal, data, npnEditor, newLicenseForm, editingLicense, actions,}) => {
    const {isOpen, onClose} = modal;
    const {selectedAgentForLicenses} = data;
    const {
        isEditingAgentNpn,
        setIsEditingAgentNpn,
        agentNpnDraft,
        setAgentNpnDraft,
        agentNpnDraftError,
        setAgentNpnDraftError,
    } = npnEditor;
    const {
        newLicenseState,
        setNewLicenseState,
        newLicenseNumber,
        setNewLicenseNumber,
        newLicenseExpiry,
        setNewLicenseExpiry,
    } = newLicenseForm;
    const {
        editingLicenseId,
        editingLicenseDraft,
        setEditingLicenseDraft,
        editingLicenseDraftError,
        setEditingLicenseDraftError,
    } = editingLicense;
    const {
        validateGlobalNpn,
        presentAlert,
        onSaveAgentNpn,
        onToggleTier2AgentActive,
        onAddAgentLicense,
        beginEditExistingLicense,
        cancelEditExistingLicense,
        saveExistingLicenseEdits,
    } = actions;
    const allStates = ALL_STATES;

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onClose}>
            <IonHeader>
                <IonToolbar color="primary">
                    <IonTitle>Manage Tier 2 Agents</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={onClose}>Close</IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                {!selectedAgentForLicenses && (
                    <IonText color="medium">
                        <p>No agent selected.</p>
                    </IonText>
                )}

                {selectedAgentForLicenses && (
                    <IonGrid>
                        <IonRow>
                            <IonCol size="12">
                                <h2 style={{ marginTop: 0, marginBottom: 4 }}>
                                    {selectedAgentForLicenses.displayName}
                                </h2>
                                <p style={{ margin: 0, marginLeft: 21 }}>
                                    <strong>Email:</strong> {selectedAgentForLicenses.email || "N/A"}
                                </p>

                                <div style={{ marginTop: 6, marginLeft: 21 }}>
                                    <strong>NPN:</strong>{" "}
                                    {!isEditingAgentNpn && (
                                        <>
                                            <span>{selectedAgentForLicenses.npnNumber || "Not set"}</span>
                                            <IonButton
                                                style={{ marginBottom: 16 }}
                                                size="small"
                                                fill="clear"
                                                className="xs-button"
                                                onClick={() => {
                                                    const current = selectedAgentForLicenses.npnNumber || "";
                                                    setIsEditingAgentNpn(true);
                                                    setAgentNpnDraft(current);
                                                    setAgentNpnDraftError(validateGlobalNpn(current));
                                                }}
                                                title="Edit NPN"
                                            >
                                                <IonIcon slot="icon-only" md={pencilOutline} ios={pencilSharp} />
                                            </IonButton>
                                        </>
                                    )}

                                    {isEditingAgentNpn && (
                                        <div
                                            style={{
                                                marginTop: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <IonInput
                                                value={agentNpnDraft}
                                                placeholder="Enter NPN"
                                                onIonInput={(e) => {
                                                    const value = (e.detail.value ?? "").toString();
                                                    setAgentNpnDraft(value);
                                                    setAgentNpnDraftError(validateGlobalNpn(value));
                                                }}
                                                style={{ maxWidth: 240 }}
                                            />
                                            <IonButton
                                                size="small"
                                                onClick={() => {
                                                    const oldNpn = selectedAgentForLicenses.npnNumber || "Not set";
                                                    const newNpn = agentNpnDraft.trim() || "Not set";

                                                    const error = validateGlobalNpn(agentNpnDraft);
                                                    setAgentNpnDraftError(error);
                                                    if (error) {
                                                        return;
                                                    }
                                                    presentAlert({
                                                        header: "Save NPN change?",
                                                        message: `NPN: ${oldNpn} → ${newNpn}`,
                                                        buttons: [
                                                            { text: "Cancel", role: "cancel" },
                                                            {
                                                                text: "Save",
                                                                role: "confirm",
                                                                handler: () => {
                                                                    void onSaveAgentNpn(agentNpnDraft);
                                                                },
                                                            },
                                                        ],
                                                    });
                                                }}
                                                disabled={!!agentNpnDraftError}
                                            >
                                                Save
                                            </IonButton>
                                            <IonButton
                                                size="small"
                                                fill="outline"
                                                onClick={() => {
                                                    setIsEditingAgentNpn(false);
                                                    setAgentNpnDraft(selectedAgentForLicenses.npnNumber || "");
                                                    setAgentNpnDraftError(
                                                        validateGlobalNpn(selectedAgentForLicenses.npnNumber || "")
                                                    );
                                                }}
                                            >
                                                Cancel
                                            </IonButton>
                                            {agentNpnDraftError && (
                                                <IonText color="danger">
                                                    <p style={{ margin: 0 }}>{agentNpnDraftError}</p>
                                                </IonText>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <p style={{ margin: "8px 0 0", marginLeft: 21 }}>
                                    <strong>States summary:</strong>{" "}
                                    {selectedAgentForLicenses.stateSummary || "No states configured"}
                                </p>

                                <IonItem lines="full" style={{ marginTop: 0 }}>
                                    <IonCol size={"3"}>
                                        <IonLabel>
                                            <strong>Agent Active:</strong>
                                        </IonLabel>
                                    </IonCol>
                                    <IonCol size={"2"}>
                                        <IonToggle
                                            checked={!!selectedAgentForLicenses.agentActive}
                                            onIonChange={(ev: ToggleCustomEvent) => {
                                                ev.target.checked = !!selectedAgentForLicenses.agentActive;

                                                const nextActive = !!ev.detail.checked;
                                                const oldStatus = selectedAgentForLicenses.agentActive ? "Active" : "Inactive";
                                                const newStatus = nextActive ? "Active" : "Inactive";

                                                presentAlert({
                                                    header: "Change agent status?",
                                                    message:
                                                        `Agent: ${selectedAgentForLicenses.displayName}\n` +
                                                        `Status: ${oldStatus} → ${newStatus}`,
                                                    buttons: [
                                                        { text: "Cancel", role: "cancel" },
                                                        {
                                                            text: "Save",
                                                            role: "confirm",
                                                            handler: () => {
                                                                void onToggleTier2AgentActive(
                                                                    selectedAgentForLicenses,
                                                                    nextActive
                                                                );
                                                            },
                                                        },
                                                    ],
                                                });
                                            }}
                                        />
                                    </IonCol>
                                </IonItem>
                            </IonCol>
                        </IonRow>

                        <IonRow style={{ marginTop: 16 }}>
                            <IonCol size="12">
                                <h3 style={{ marginTop: 0 }}>Add New License</h3>
                            </IonCol>
                        </IonRow>

                        <IonRow>
                            <IonCol size="12" sizeMd="4">
                                <IonItem>
                                    <IonLabel position="stacked">State</IonLabel>
                                    <IonSelect
                                        value={newLicenseState}
                                        placeholder="Select state"
                                        interface="popover"
                                        onIonChange={(e) =>
                                            setNewLicenseState((e.detail.value || "").toString())
                                        }
                                    >
                                        {allStates.map((state) => (
                                            <IonSelectOption key={state.code} value={state.code}>
                                                {state.code} - {state.name}
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>
                            </IonCol>

                            <IonCol size="12" sizeMd="4">
                                <IonItem>
                                    <IonLabel position="stacked">License Number</IonLabel>
                                    <IonInput
                                        value={newLicenseNumber}
                                        placeholder="Enter license number"
                                        onIonChange={(e) =>
                                            setNewLicenseNumber((e.detail.value || "").toString())
                                        }
                                    />
                                </IonItem>
                            </IonCol>

                            <IonCol size="12" sizeMd="4">
                                <IonItem>
                                    <IonLabel position="stacked">Expiration Date</IonLabel>
                                    <IonInput
                                        type="date"
                                        value={newLicenseExpiry}
                                        onIonChange={(e) =>
                                            setNewLicenseExpiry((e.detail.value || "").toString())
                                        }
                                    />
                                </IonItem>
                            </IonCol>
                        </IonRow>

                        <IonRow style={{ marginTop: 8 }}>
                            <IonCol size="12" className="ion-text-center ion-text-md-right">
                                <IonButton
                                    onClick={() => void onAddAgentLicense()}
                                    disabled={!newLicenseState || !newLicenseNumber}
                                >
                                    Add License
                                </IonButton>
                            </IonCol>
                        </IonRow>

                        <IonRow style={{ marginTop: 16 }}>
                            <IonCol size="12">
                                <IonAccordionGroup>
                                    <IonAccordion value="existing-licenses">
                                        <IonItem slot="header" color="light" lines="full">
                                            <IonLabel>Existing Licenses</IonLabel>
                                            <IonBadge slot="end">
                                                {selectedAgentForLicenses.licenses?.length || 0}
                                            </IonBadge>
                                        </IonItem>

                                        <div slot="content" style={{ padding: "8px 0 0" }}>
                                            {(selectedAgentForLicenses.licenses?.length || 0) === 0 && (
                                                <IonItem lines="none">
                                                    <IonLabel>No licenses for this agent.</IonLabel>
                                                </IonItem>
                                            )}

                                            {(selectedAgentForLicenses.licenses?.length || 0) > 0 && (
                                                <div
                                                    style={{
                                                        maxHeight: "260px",
                                                        overflowY: "auto",
                                                        border: "1px solid #ddd",
                                                        borderRadius: 4,
                                                        padding: "4px 0",
                                                    }}
                                                >
                                                    <IonList>
                                                        {selectedAgentForLicenses.licenses!.map((license) => {
                                                            const isEditing =
                                                                editingLicenseId === license.id &&
                                                                !!editingLicenseDraft;

                                                            return (
                                                                <IonItem key={license.id} lines="full">
                                                                    {!isEditing && (
                                                                        <IonGrid style={{ width: "100%" }}>
                                                                            <IonRow className="ion-align-items-center">
                                                                                <IonCol size="12" sizeMd="1">
                                                                                    <strong>{license.stateCode}</strong>
                                                                                </IonCol>
                                                                                <IonCol size="12" sizeMd="3">
                                                                                    <div>
                                                                                        <strong>License #:</strong>{" "}
                                                                                        {license.licenseNumber}
                                                                                    </div>
                                                                                </IonCol>
                                                                                <IonCol size="12" sizeMd="4">
                                                                                    <div>
                                                                                        <strong>Exp:</strong>{" "}
                                                                                        {license.expirationDate ? license.expirationDate : "None"}
                                                                                    </div>
                                                                                </IonCol>
                                                                                <IonCol size="12" sizeMd="3">
                                                                                    <div>
                                                                                        <strong>
                                                                                            {license.active ? "Active" : "Inactive"}
                                                                                        </strong>
                                                                                    </div>
                                                                                </IonCol>
                                                                                <IonCol
                                                                                    size="12"
                                                                                    sizeMd="1"
                                                                                    className="ion-text-right"
                                                                                >
                                                                                    <IonButton
                                                                                        size="small"
                                                                                        fill="clear"
                                                                                        className="xs-button"
                                                                                        onClick={() => beginEditExistingLicense(license)}
                                                                                        title="Edit license"
                                                                                    >
                                                                                        <IonIcon
                                                                                            slot="icon-only"
                                                                                            md={pencilOutline}
                                                                                            ios={pencilSharp}
                                                                                        />
                                                                                    </IonButton>
                                                                                </IonCol>
                                                                            </IonRow>
                                                                        </IonGrid>
                                                                    )}

                                                                    {isEditing && editingLicenseDraft && (
                                                                        <IonGrid style={{ width: "100%" }}>
                                                                            <IonRow className="ion-align-items-end">
                                                                                <IonCol size="12" sizeMd="3">
                                                                                    <IonItem lines="full">
                                                                                        <IonLabel position="stacked">
                                                                                            State
                                                                                        </IonLabel>
                                                                                        <IonSelect
                                                                                            value={editingLicenseDraft.stateCode}
                                                                                            interface="popover"
                                                                                            onIonChange={(e) => {
                                                                                                const value = (e.detail.value || "").toString();
                                                                                                setEditingLicenseDraft(
                                                                                                    (prev) =>
                                                                                                        prev ? {...prev, stateCode: value,} : prev
                                                                                                );
                                                                                                setEditingLicenseDraftError("");
                                                                                            }}
                                                                                        >
                                                                                            {allStates.map((state) => (
                                                                                                <IonSelectOption key={state.code} value={state.code}>
                                                                                                    {state.code} - {state.name}
                                                                                                </IonSelectOption>
                                                                                            ))}
                                                                                        </IonSelect>
                                                                                    </IonItem>
                                                                                </IonCol>

                                                                                <IonCol size="12" sizeMd="3">
                                                                                    <IonItem lines="full">
                                                                                        <IonLabel position="stacked">
                                                                                            License #
                                                                                        </IonLabel>
                                                                                        <IonInput
                                                                                            value={editingLicenseDraft.licenseNumber}
                                                                                            onIonChange={(e) => {
                                                                                                const value = (e.detail.value || "").toString();
                                                                                                setEditingLicenseDraft(
                                                                                                    (prev) =>
                                                                                                        prev ? {...prev, licenseNumber: value,} : prev
                                                                                                );
                                                                                                setEditingLicenseDraftError("");
                                                                                            }}
                                                                                        />
                                                                                    </IonItem>
                                                                                </IonCol>

                                                                                <IonCol size="12" sizeMd="4">
                                                                                    <IonItem lines="full">
                                                                                        <IonLabel position="stacked">
                                                                                            Expiration Date
                                                                                        </IonLabel>
                                                                                        <IonInput
                                                                                            type="date"
                                                                                            value={editingLicenseDraft.expirationDate}
                                                                                            onIonChange={(e) => {
                                                                                                const value = (e.detail.value || "").toString();
                                                                                                setEditingLicenseDraft(
                                                                                                    (prev) =>
                                                                                                        prev ? {...prev, expirationDate: value,} : prev
                                                                                                );
                                                                                                setEditingLicenseDraftError("");
                                                                                            }}
                                                                                        />
                                                                                    </IonItem>
                                                                                </IonCol>

                                                                                <IonCol size="12" sizeMd="2">
                                                                                    <IonLabel className="tier2agent-state-license-active">
                                                                                        Active
                                                                                    </IonLabel>
                                                                                    <IonItem lines="full">
                                                                                        <IonToggle
                                                                                            checked={!!editingLicenseDraft.active}
                                                                                            onIonChange={(e) => {
                                                                                                setEditingLicenseDraft(
                                                                                                    (prev) =>
                                                                                                        prev ? {...prev, active: e.detail.checked,} : prev
                                                                                                );
                                                                                                setEditingLicenseDraftError("");
                                                                                            }}
                                                                                        />
                                                                                    </IonItem>
                                                                                </IonCol>
                                                                            </IonRow>

                                                                            {editingLicenseDraftError && (
                                                                                <IonRow style={{ marginTop: 6 }}>
                                                                                    <IonCol size="12">
                                                                                        <IonText color="danger">
                                                                                            <p style={{ margin: 0 }}>
                                                                                                {editingLicenseDraftError}
                                                                                            </p>
                                                                                        </IonText>
                                                                                    </IonCol>
                                                                                </IonRow>
                                                                            )}

                                                                            <IonRow style={{ marginTop: 8 }}>
                                                                                <IonCol size="12" className="ion-text-right">
                                                                                    <IonButton
                                                                                        size="small"
                                                                                        onClick={() => void saveExistingLicenseEdits(license)}
                                                                                    >
                                                                                        Save
                                                                                    </IonButton>
                                                                                    <IonButton
                                                                                        size="small"
                                                                                        fill="outline"
                                                                                        onClick={cancelEditExistingLicense}
                                                                                        style={{ marginLeft: 8 }}
                                                                                    >
                                                                                        Cancel
                                                                                    </IonButton>
                                                                                </IonCol>
                                                                            </IonRow>
                                                                        </IonGrid>
                                                                    )}
                                                                </IonItem>
                                                            );
                                                        })}
                                                    </IonList>
                                                </div>
                                            )}
                                        </div>
                                    </IonAccordion>
                                </IonAccordionGroup>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                )}
            </IonContent>
        </IonModal>
    );
};

export default ModalManageTier2Licenses;
